import { useLayoutEffect } from 'react';

interface ThemeColors {
    primaryColor: string;
    textColor: string;
    /**
     * Scopes CSS variables to this element instead of document.documentElement.
     * Pass via a callback ref / useState so React re-runs the effect when the
     * element mounts or unmounts. `null` = not yet mounted, skip entirely.
     * `undefined` (default) falls back to document.documentElement.
     */
    element?: HTMLElement | null;
}

export const useThemeColors = ({ primaryColor, textColor, element }: ThemeColors): void => {
    useLayoutEffect(() => {
        if (!primaryColor) return;
        // null means the element was provided but isn't mounted yet (e.g. a closed
        // dialog). Don't fall back to document.documentElement in that case.
        if (element === null) return;

        const root = element ?? document.documentElement;

        root.style.setProperty('--color-surface-theme', primaryColor);
        root.style.setProperty('--color-theme-default', primaryColor);
        root.style.setProperty('--color-text-on-theme-primary', textColor);
        root.style.setProperty('--color-border-theme', primaryColor);

        root.style.setProperty('--color-intent-primary-default', primaryColor);
        root.style.setProperty('--color-intent-on-primary-default', textColor);
        root.style.setProperty('--color-intent-primary-hover', `color-mix(in srgb, ${primaryColor} 85%, black)`);
        root.style.setProperty('--color-intent-primary-pressed', `color-mix(in srgb, ${primaryColor} 70%, black)`);
        root.style.setProperty('--color-intent-primary-disabled', `color-mix(in srgb, ${primaryColor} 50%, gray)`);

        return () => {
            const vars = [
                '--color-surface-theme',
                '--color-theme-default',
                '--color-text-on-theme-primary',
                '--color-border-theme',
                '--color-intent-primary-default',
                '--color-intent-on-primary-default',
                '--color-intent-primary-hover',
                '--color-intent-primary-pressed',
                '--color-intent-primary-disabled',
            ];
            vars.forEach((v) => root.style.removeProperty(v));
        };
    }, [primaryColor, textColor, element]);
};
